import { motion } from 'framer-motion';
import { MapPin, Mail, Phone, Clock, ExternalLink, Send, Home, CheckCircle, Leaf } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';

const T = {
  heroLabel: { de: 'Kontakt', en: 'Contact' },
  heroTitle1: { de: 'Kostenlose', en: 'Free' },
  heroTitle2: { de: 'Besichtigung', en: 'Inspection' },
  heroSub: { de: 'Kontaktieren Sie uns für eine unverbindliche Besichtigung und ein faires Festpreis-Angebot.', en: 'Contact us for a non-binding inspection and a fair fixed-price quote.' },
  address: { de: 'Adresse', en: 'Address' },
  email: { de: 'E-Mail', en: 'Email' },
  phone: { de: 'Telefon', en: 'Phone' },
  website: { de: 'Website', en: 'Website' },
  availability: { de: 'Erreichbarkeit', en: 'Availability' },
  availVal: { de: 'Mo–Fr: 07:00 – 18:00 Uhr | Sa: nach Vereinbarung', en: 'Mon–Fri: 07:00 – 18:00 | Sat: by appointment' },
  formTitle: { de: 'Anfrage senden', en: 'Send Enquiry' },
  formSub: { de: 'Beschreiben Sie Ihr Anliegen und wir melden uns innerhalb von 24 Stunden.', en: 'Describe your needs and we will get back to you within 24 hours.' },
  name: { de: 'Name *', en: 'Name *' },
  emailLabel: { de: 'E-Mail *', en: 'Email *' },
  phonePh: { de: 'Telefon', en: 'Phone' },
  type: { de: 'Art der Anfrage', en: 'Type of Enquiry' },
  typePh: { de: 'Bitte wählen...', en: 'Please select...' },
  addressLabel: { de: 'Adresse des Objekts', en: 'Property Address' },
  addressPh: { de: 'Straße, PLZ, Ort', en: 'Street, postcode, city' },
  message: { de: 'Ihre Nachricht *', en: 'Your Message *' },
  msgPh: { de: 'Beschreiben Sie Ihr Anliegen...', en: 'Describe your needs...' },
  submit: { de: 'Anfrage senden', en: 'Send Enquiry' },
  thankYou: { de: 'Vielen Dank für Ihre Anfrage! Wir melden uns innerhalb von 24 Stunden bei Ihnen, um einen Besichtigungstermin zu vereinbaren.', en: 'Thank you for your enquiry! We will get back to you within 24 hours to arrange an inspection appointment.' },
};

export default function AtoBObjektserviceKontakt() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', type: '', address: '', message: '' });
  const { language } = useLanguage();

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(T.thankYou[language]);
  };

  return (
    <main data-testid="sub-atob-objektservice-kontakt" className="bg-[#f8f7f5]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-2 mb-6">
              <Leaf size={18} className="text-[#F5A623]" />
              <span className="text-[#F5A623] text-sm font-semibold tracking-[0.15em] uppercase">{T.heroLabel[language]}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0F1A2E] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {T.heroTitle1[language]}<br /><span className="text-[#F5A623]">{T.heroTitle2[language]}</span>
            </h1>
            <p className="text-xl text-[#52525b]">{T.heroSub[language]}</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl border border-[#e5e5e5] p-8 md:p-10 shadow-lg shadow-[#0F1A2E]/5">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-[#0F1A2E] rounded-xl flex items-center justify-center">
                    <Home size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#0F1A2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>AtoB Objektservice GmbH</h2>
                    <p className="text-[#52525b] text-sm">Teil der Basic United Holding</p>
                  </div>
                </div>
                
                <div className="space-y-6 mb-8">
                  <ContactItem icon={MapPin} label="Adresse" value="Hanauer Landstraße 291A, 60314 Frankfurt am Main" />
                  <ContactItem icon={Mail} label="E-Mail" value="info@atob-objektservice.de" link="mailto:info@atob-objektservice.de" />
                  <ContactItem icon={Phone} label="Telefon" value="02173 2046452" link="tel:+4921732046452" />
                  <ContactItem icon={ExternalLink} label="Website" value="www.atob-objektservice.de" link="https://www.atob-objektservice.de" />
                  <ContactItem icon={Clock} label="Erreichbarkeit" value="Mo–Fr: 08:00 – 17:00 Uhr | Sa: nach Vereinbarung" />
                </div>

                <div className="pt-6 border-t border-[#e5e5e5]">
                  <p className="text-[#52525b] text-xs uppercase tracking-wider mb-2">Geschäftsführung</p>
                  <p className="text-[#0F1A2E] font-semibold">Admir Basic & Alessandro Costantino</p>
                </div>
              </div>

              {/* Benefits */}
              <div className="mt-8 grid grid-cols-2 gap-4">
                {[
                  'Kostenlose Besichtigung',
                  'Festpreis-Garantie',
                  'Kurzfristige Termine',
                  '90%+ Recycling-Quote',
                ].map((benefit, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-[#e5e5e5] flex items-center gap-3">
                    <CheckCircle size={18} className="text-[#F5A623]" />
                    <span className="text-[#0F1A2E] text-sm font-medium">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="mt-8 rounded-2xl overflow-hidden border border-[#e5e5e5] h-[200px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2558.5!2d8.71!3d50.11!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTDCsDA2JzM2LjAiTiA4wrA0MicyMi44IkU!5e0!3m2!1sde!2sde!4v1!5m2!1sde!2sde"
                  className="w-full h-full border-0"
                  allowFullScreen=""
                  loading="lazy"
                  title="Standort AtoB Objektservice"
                ></iframe>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl border border-[#e5e5e5] p-8 md:p-10 shadow-lg shadow-[#0F1A2E]/5">
                <h3 className="text-2xl font-bold text-[#0F1A2E] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Besichtigung anfragen
                </h3>
                <p className="text-[#52525b] mb-8">Füllen Sie das Formular aus und wir vereinbaren einen Termin.</p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-[#0F1A2E] text-sm font-medium mb-2">Ihr Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-[#f8f7f5] border border-[#e5e5e5] rounded-xl px-4 py-3 text-[#0F1A2E] placeholder-[#94a3b8] focus:border-[#F5A623] focus:outline-none transition-colors"
                      placeholder="Max Mustermann"
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#0F1A2E] text-sm font-medium mb-2">E-Mail *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-[#f8f7f5] border border-[#e5e5e5] rounded-xl px-4 py-3 text-[#0F1A2E] placeholder-[#94a3b8] focus:border-[#F5A623] focus:outline-none transition-colors"
                        placeholder="ihre@email.de"
                      />
                    </div>
                    <div>
                      <label className="block text-[#0F1A2E] text-sm font-medium mb-2">Telefon *</label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full bg-[#f8f7f5] border border-[#e5e5e5] rounded-xl px-4 py-3 text-[#0F1A2E] placeholder-[#94a3b8] focus:border-[#F5A623] focus:outline-none transition-colors"
                        placeholder="+49 ..."
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-[#0F1A2E] text-sm font-medium mb-2">Art der Räumung</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full bg-[#f8f7f5] border border-[#e5e5e5] rounded-xl px-4 py-3 text-[#0F1A2E] focus:border-[#F5A623] focus:outline-none transition-colors"
                    >
                      <option value="">Bitte wählen...</option>
                      <option value="haushaltsaufloesung">Haushaltsauflösung</option>
                      <option value="entruempelung">Entrümpelung (Keller/Dachboden)</option>
                      <option value="nachlassraeumung">Nachlassräumung</option>
                      <option value="gewerbe">Gewerberäumung</option>
                      <option value="sonstiges">Sonstiges</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-[#0F1A2E] text-sm font-medium mb-2">Adresse des Objekts</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full bg-[#f8f7f5] border border-[#e5e5e5] rounded-xl px-4 py-3 text-[#0F1A2E] placeholder-[#94a3b8] focus:border-[#F5A623] focus:outline-none transition-colors"
                      placeholder="Straße, PLZ, Ort"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-[#0F1A2E] text-sm font-medium mb-2">Weitere Informationen</label>
                    <textarea
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-[#f8f7f5] border border-[#e5e5e5] rounded-xl px-4 py-3 text-[#0F1A2E] placeholder-[#94a3b8] focus:border-[#F5A623] focus:outline-none transition-colors resize-none"
                      placeholder="Größe, Zustand, besondere Anforderungen..."
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-[#F5A623] text-white py-4 font-bold tracking-wider uppercase flex items-center justify-center gap-2 hover:bg-[#0F1A2E] transition-colors rounded-xl shadow-lg shadow-[#F5A623]/20"
                  >
                    Besichtigung anfragen <Send size={18} />
                  </button>
                </form>
              </div>

              {/* Phone CTA */}
              <div className="mt-6 bg-[#0F1A2E] rounded-2xl p-6 text-center">
                <p className="text-white/70 text-sm mb-2">Lieber direkt anrufen?</p>
                <a href="tel:+4921732046452" className="text-white text-2xl font-bold flex items-center justify-center gap-3 hover:text-[#F5A623] transition-colors">
                  <Phone size={24} />
                  02173 2046452
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}

function ContactItem({ icon: Icon, label, value, link }) {
  const Content = () => (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 bg-[#F5A623]/10 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon size={18} className="text-[#F5A623]" />
      </div>
      <div>
        <p className="text-[#52525b] text-xs uppercase tracking-wider mb-1">{label}</p>
        <p className="text-[#0F1A2E] font-medium">{value}</p>
      </div>
    </div>
  );

  if (link) {
    return (
      <a href={link} target={link.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="block hover:opacity-70 transition-opacity">
        <Content />
      </a>
    );
  }
  return <Content />;
}
